import { getClerkSession } from '@lib/auth/clerk-session-bridge';
import {
  isSameOriginApiFetch,
  pathnameOfFetchInput,
  shouldAttachBearer,
} from '@lib/auth/utils/should-attach-bearer';
import { withAuthHeader } from '@lib/auth/utils/with-auth-header';
import { observeAuthedFetch } from '@lib/observability/authed-fetch-tags/observe-authed-fetch.client';
import 'client-only';

const INSTALLED_FLAG = '__claiAuthedFetchInstalled';

function isAuthedFetchEnabled(): boolean {
  return process.env.NEXT_PUBLIC_DISABLE_AUTHED_FETCH_BEARER !== 'true';
}

function headerInitOf(
  input: RequestInfo | URL,
  init?: RequestInit
): RequestInit | Request | undefined {
  if (init !== undefined) return init;
  return input instanceof Request ? input : undefined;
}

/**
 * Installs a global `fetch` wrapper that attaches a fresh Clerk Bearer token
 * to same-origin `/api/**` requests. Idempotent and env-gated; every abnormal
 * path falls through to the original fetch unchanged.
 */
export function installAuthedFetch(): void {
  if (!isAuthedFetchEnabled()) return;

  const flagged = globalThis as typeof globalThis & {
    [INSTALLED_FLAG]?: boolean;
  };
  if (flagged[INSTALLED_FLAG]) return;
  flagged[INSTALLED_FLAG] = true;

  const original = globalThis.fetch.bind(globalThis);

  globalThis.fetch = async (input, init) => {
    try {
      const pathname = pathnameOfFetchInput(input);

      if (!shouldAttachBearer(input)) {
        if (isSameOriginApiFetch(input)) {
          observeAuthedFetch('skipped_not_allowlisted', pathname);
        }
        return original(input, init);
      }

      const session = getClerkSession();
      if (!session) {
        observeAuthedFetch('skipped_no_session', pathname);
        return original(input, init);
      }

      let token: string | null;
      try {
        token = await session.getToken();
      } catch {
        observeAuthedFetch('skipped_null_token', pathname);
        return original(input, init);
      }

      if (!token) {
        observeAuthedFetch('skipped_null_token', pathname);
        return original(input, init);
      }

      observeAuthedFetch('attached', pathname);
      return original(input, withAuthHeader(headerInitOf(input, init), token));
    } catch {
      return original(input, init);
    }
  };
}

/** Clears the install sentinel between Vitest cases. */
export function resetAuthedFetchInstallForTests(): void {
  delete (globalThis as Record<string, unknown>)[INSTALLED_FLAG];
}
